/**
 * Загрузка вложений объекта: синхронный POST, presigned PUT, S3 multipart.
 * Пороги согласованы с FILESTORE_SYNC_MAX_BYTES (бэк по умолчанию 8 МиБ) и
 * VITE_FILESTORE_USE_MULTIPART_MIN_BYTES (когда тянуть multipart вместо одного PUT).
 */

import { objectsApi } from "@/services/api-v2.service";
import { readFetchErrorMessage } from "@/lib/geometry-api-errors";
import { del, getToken } from "@/lib/api-client-v2";

const MB = 1024 * 1024;

function envInt(name: string, def: number): number {
  const v = (import.meta as ImportMeta & { env?: Record<string, string> }).env?.[name];
  if (v == null || v === "") return def;
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : def;
}

/** Синхрон с бэкендом FILESTORE_SYNC_MAX_BYTES (по умолчанию 8 МиБ). */
export const FILESTORE_SYNC_MAX_BYTES = envInt("VITE_FILESTORE_SYNC_MAX_BYTES", 8 * MB);

/**
 * Файлы больше этого размера идут через Multipart, меньше — одним presigned PUT.
 * По умолчанию 512 МиБ (снижает риск OOM в браузере на одной большой куче).
 */
export const FILESTORE_USE_MULTIPART_MIN_BYTES = envInt(
  "VITE_FILESTORE_USE_MULTIPART_MIN_BYTES",
  512 * MB,
);

const S3_SINGLE_OBJECT_MAX = 5 * 1024 * 1024 * 1024;

function partSizeForFileSize(bytes: number): number {
  const pmin = 5 * MB;
  const pmax = 5 * 1024 * 1024 * 1024;
  const nTarget = 9500;
  const need = Math.ceil(bytes / nTarget);
  return Math.max(pmin, Math.min(pmax, Math.max(need, pmin)));
}

function putPresigned(
  url: string,
  file: File,
  onPartProgress: (loaded: number, total: number) => void,
): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    if (file.type) xhr.setRequestHeader("Content-Type", file.type);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPartProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) resolve();
      else reject(new Error(String(xhr.status)));
    };
    xhr.onerror = () => reject(new Error("Network error (presigned PUT)"));
    xhr.send(file);
  });
}

function putPresignedPart(
  url: string,
  body: Blob,
  onPartProgress: (loaded: number, total: number) => void,
): Promise<string | null> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("PUT", url);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onPartProgress(e.loaded, e.total);
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        const etag = xhr.getResponseHeader("ETag");
        resolve(etag);
      } else reject(new Error(String(xhr.status)));
    };
    xhr.onerror = () => reject(new Error("Network error (multipart part)"));
    xhr.send(body);
  });
}

function overallProgress(
  onProgress: (n: number) => void,
  partIndex: number,
  partCount: number,
  loaded: number,
  partTotal: number,
): void {
  const base = (partIndex - 1) / partCount;
  const frac = partTotal > 0 ? loaded / partTotal : 0;
  onProgress(Math.min(100, Math.round(100 * (base + frac / partCount))));
}

export interface UploadObjectFileOptions {
  objectId: number;
  file: File;
  parentNodeId: number | null;
  onProgress: (percent: number) => void;
  /** description / geometry_id при появлении в UI */
  description?: string | null;
  geometryId?: number | null;
}

/**
 * Единая точка загрузки: FormData, presigned или multipart.
 */
export async function uploadObjectFile(opts: UploadObjectFileOptions): Promise<void> {
  if (opts.file.size > S3_SINGLE_OBJECT_MAX) {
    throw new Error("Файл слишком велик: максимум 5 ГиБ за один объект (ограничение S3).");
  }

  if (opts.file.size <= FILESTORE_SYNC_MAX_BYTES) {
    return uploadViaFormData(opts);
  }

  if (opts.file.size < FILESTORE_USE_MULTIPART_MIN_BYTES) {
    return uploadViaPresigned(opts);
  }

  return uploadViaMultipart(opts);
}

async function uploadViaFormData({
  objectId,
  file,
  parentNodeId,
  onProgress,
  description,
  geometryId,
}: UploadObjectFileOptions): Promise<void> {
  const formData = new FormData();
  formData.append("file", file);
  if (parentNodeId != null) formData.append("parent_node_id", String(parentNodeId));
  if (description != null && description !== "") formData.append("description", description);
  if (geometryId != null) formData.append("geometry_id", String(geometryId));

  const token = getToken();
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", `/api/v1/objects/${objectId}/files`);
    if (token) xhr.setRequestHeader("Authorization", `Bearer ${token}`);
    xhr.upload.onprogress = (e) => {
      if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100));
    };
    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        onProgress(100);
        resolve();
        return;
      }
      const ct = xhr.getResponseHeader("content-type") || "application/json";
      const r = new Response(xhr.responseText, {
        status: xhr.status,
        headers: { "content-type": ct },
      });
      readFetchErrorMessage(r)
        .then((msg) => reject(new Error(msg || "Ошибка загрузки")))
        .catch(() => reject(new Error(`HTTP ${xhr.status}`)));
    };
    xhr.onerror = () => reject(new Error("Network error"));
    xhr.send(formData);
  });
}

async function uploadViaPresigned({
  objectId,
  file,
  parentNodeId,
  onProgress,
  description,
  geometryId,
}: UploadObjectFileOptions): Promise<void> {
  let init;
  try {
    init = await objectsApi.filestorePresignInit(objectId, {
      name: file.name,
      size_bytes: file.size,
      content_type: file.type || "application/octet-stream",
      parent_node_id: parentNodeId ?? null,
      description: description ?? undefined,
      geometry_id: geometryId ?? null,
    });
  } catch (e) {
    const msg = e instanceof Error ? e.message : "presign-init";
    throw new Error(msg);
  }

  let last = 0;
  await putPresigned(init.upload_url, file, (loaded, total) => {
    if (total > 0) {
      const p = Math.round((loaded / total) * 100);
      if (p !== last) {
        last = p;
        onProgress(p);
      }
    }
  });
  onProgress(100);
  await objectsApi.filestorePresignComplete(objectId, { blob_id: init.blob_id });
}

async function uploadViaMultipart({
  objectId,
  file,
  parentNodeId,
  onProgress,
  description,
  geometryId,
}: UploadObjectFileOptions): Promise<void> {
  const partSize = partSizeForFileSize(file.size);
  const init = await objectsApi.filestoreMultipartInit(objectId, {
    name: file.name,
    size_bytes: file.size,
    content_type: file.type || "application/octet-stream",
    part_size_bytes: partSize,
    parent_node_id: parentNodeId ?? null,
    description: description ?? undefined,
    geometry_id: geometryId ?? null,
  });

  const { blob_id, part_size_bytes, part_count } = init;
  const ps = part_size_bytes;
  const parts: { part_number: number; etag: string }[] = [];

  try {
    for (let batchStart = 1; batchStart <= part_count; batchStart += 32) {
      const batch: number[] = [];
      for (let n = batchStart; n < batchStart + 32 && n <= part_count; n++) batch.push(n);
      const { parts: items } = await objectsApi.filestoreMultipartPartUrls(
        objectId,
        blob_id,
        { part_numbers: batch },
      );
      for (const item of items) {
        const pn = item.part_number;
        const start = (pn - 1) * ps;
        const end = pn === part_count ? file.size : Math.min(start + ps, file.size);
        const chunk = file.slice(start, end);
        const url = item.put_url;
        const etag = await putPresignedPart(url, chunk, (l, t) => {
          overallProgress(onProgress, pn, part_count, l, t);
        });
        if (!etag) {
          throw new Error("S3: нет ETag в ответе части");
        }
        parts.push({ part_number: pn, etag: etag.replace(/^"+|"+$/g, "") });
      }
    }

    onProgress(100);
    const sorted = [...parts].sort((a, b) => a.part_number - b.part_number);
    await objectsApi.filestoreMultipartComplete(objectId, blob_id, { parts: sorted });
  } catch (e) {
    try {
      await del<void>(`/objects/${objectId}/files/multipart/${blob_id}`);
    } catch {
      /* best-effort: AbortMultipart + удаление узла в бэке */
    }
    throw e;
  }
}
